import { faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons/faRectangleVerticalHistory";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, useTheme } from '@mui/material';
import { FEED_CARD_WIDTH } from "app/appConstants";
import { MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport } from 'components';
import { MprcFilterCountInfo } from 'components/filter';
import { FeedDataDto, FeedItemDto, FeedQuestionQueryOrderBy, FeedType } from 'generated/CoreApi_v1';
import { usePageTitle } from "hooks/usePageTitle";
import { useWindowSize } from 'hooks/useWindowSize';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useGobackOrKnowledge } from "views/common/hooks/useGobackOrHome";
import { FeedCustomEventProps, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { FeedQuestionFilter, IFeedQuestionFilterOptions } from "views/knowledge/publication/pages/Question/FeedQuestionFilter";
import { RightPanelContentPage } from "../common/RightPanelContentPage";
import { PublicationCardHeader } from "../components/PublicationCardHeader";
import { PublicationHeader } from '../components/PublicationHeader';
import { FeedQuestionBox } from "./FeedQuestionBox";

////////////////////////////////////////////////////////////////////////////////////////////////

function isFiltered(filter?: IFeedQuestionFilterOptions): boolean {
    return (filter?.searchText?.length || 0) > 0;
}

////////////////////////////////////////////////////////////////////////////////////////////////

export const QuestionViewer: React.FC = () => {

    const { id, answerId } = useParams();
    const publicationId = id;

    const theme = useTheme();
    const api = useCoreApi();

    const location = useLocation();
    const gobackOrHome = useGobackOrKnowledge();

    const { t } = useTranslation("knowledge");
    const { width } = useWindowSize({ wait: 50 });
    const [searchParams, setSearchParams] = useSearchParams();

    const [publication, setPublication] = useState<FeedItemDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [count, setCount] = useState<number | null>();

    usePageTitle(t("Feed.Question.PageTitle"));

    // fn é enviado pela tela de notificações
    const filtered = useRef<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<IFeedQuestionFilterOptions>(location.state?.filter ?? {
        orderBy: searchParams.has("fn") == null ? FeedQuestionQueryOrderBy.BestRated : FeedQuestionQueryOrderBy.MostRecent,
        searchText: ""
    });

    ///
    const handleFilterChange = useCallback((filter: IFeedQuestionFilterOptions) => {
        filtered.current = isFiltered(filter);
        setFilterOptions(filter);
        setCount(null);
    }, []);

    ///
    const handleRefreshData = (data?: FeedDataDto, resetFilter?: boolean) => {
        if (resetFilter) {
            setFilterOptions({
                orderBy: FeedQuestionQueryOrderBy.BestRated,
                searchText: ""
            });
            setCount(null);
        }
        setCount(data?.totalRows ?? 0);
    };

    ///
    const handleNewItem = (e: FeedCustomEventProps) => {
        if (publicationId) {
            setLoading(true);
            load(publicationId);
        }
    };

    ///
    const load = async (publicationId: string) => {
        let markRead = true;

        // Se a solicitação foi realizada a partir do Feed de Histórico,
        // não deve marcar a Publicação como 'Lida', para não alterar o histórico de visualização.
        if (location.state?.hasOwnProperty("previousPathname")) {
            const previousPathname = location.state.previousPathname as string;
            markRead = !previousPathname.startsWith(KnowledgePaths.FeedHistory);
        }

        try {
            const result = await api.v1.knowledgeFeed.getFeedItem({ publicationId, markRead });
            const record = result.data;

            setPublication(record);
        }
        catch (error: any) {
            setViewError(error);
        }
        finally {
            setLoading(false);
        }
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (publicationId != null) {
            load(publicationId);
        }

        if (searchParams.has('fn')) {
            searchParams.delete('fn');
            setSearchParams(searchParams, { replace: true });
        }

        subscribe("PublicationRestored", () => load(publicationId!));
        subscribe("PublicationPermanentlyDeleted", gobackOrHome);
        subscribe("AnswerCreated", handleNewItem);
        return () => {
            unsubscribe("PublicationRestored", () => load(publicationId!));
            unsubscribe("PublicationPermanentlyDeleted", gobackOrHome);
            unsubscribe("AnswerCreated", handleNewItem);
        };
    }, []);

    ///
    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values["md"])
            setOpenRightPanel(false);
    }, [width]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderCardHeader = useCallback(() => {
        return (
            publication ?
                <Box sx={{ width: FEED_CARD_WIDTH, margin: "0 auto" }}>
                    <MprcContainer margin="dense">
                        <PublicationCardHeader
                            feedType={FeedType.ContentPage}
                            publication={publication}
                            onNeedResetRowHeight={() => { }}
                            onReload={() => load(publicationId!)}
                            onFeedItemChanged={
                                (data) => {
                                    if (data.deletedOn != null) gobackOrHome();
                                    else load(publicationId!);
                                }
                            }
                        />
                        <FeedQuestionFilter
                            answerCount={publication.questionDefinition?.answerCount ?? 0}
                            filterOptions={filterOptions}
                            onFilterChange={handleFilterChange}
                        />
                    </MprcContainer>
                    {
                        count != undefined && filtered.current &&
                        <MprcFilterCountInfo
                            variant={"bodyNotice"}
                            text={t("Feed.Question.FilterInfoMessage", { count: count ?? 0 })}
                        />
                    }
                </Box>
                : <Box></Box>
        );
    }, [publication, filterOptions, count]);

    ///
    return (
        <>
            {
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading
                        ? <MprcInfiniteLoading visible />
                        : <>
                            <MprcContainer position="sticky" margin="none">
                                <PublicationHeader
                                    onClose={gobackOrHome}
                                    actions={
                                        width < theme.breakpoints.values["md"]
                                            ? [<IconButton onClick={() => setOpenRightPanel(state => !state)}><FontAwesomeIcon icon={faRectangleVerticalHistory} /></IconButton>]
                                            : []
                                    }
                                />
                            </MprcContainer>
                            <FeedQuestionBox
                                rightPanelBreakpoint="md"
                                publication={publication!}
                                feedType={FeedType.Answers}
                                cardHeader={RenderCardHeader()}
                                rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                                filter={filterOptions}
                                answerId={answerId}
                                count={count}
                                onRefreshData={handleRefreshData}
                                openRightPanel={openRightPanel}
                                onCloseRightPanel={() => setOpenRightPanel(false)}
                                onGoBackStart={() => setCount(null)}
                            />
                        </>
            }
        </>
    );
};
